import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Activity, AlertTriangle, ArrowLeft, Bot, Cpu, Loader2, Microscope, RefreshCw, Sparkles } from 'lucide-react';

const toneClasses = {
    good: 'bg-green-50 text-green-800 border-green-200',
    medium: 'bg-amber-50 text-amber-800 border-amber-200',
    low: 'bg-blue-50 text-blue-800 border-blue-200',
    high: 'bg-red-50 text-red-800 border-red-200'
};

const formatDate = (value) => {
    try {
        return new Date(value).toLocaleString('tr-TR');
    } catch (error) {
        return value || '-';
    }
};

const severityLabel = (value) => {
    if (value === 'high') return 'Yüksek';
    if (value === 'medium') return 'Orta';
    if (value === 'good') return 'İyi';
    return 'Düşük';
};

const serviceStatusClasses = (value) => {
    if (value === 'ok') return 'bg-green-50 text-green-800 border-green-200';
    if (value === 'fallback') return 'bg-amber-50 text-amber-800 border-amber-200';
    if (value === 'degraded') return 'bg-red-50 text-red-800 border-red-200';
    return 'bg-gray-50 text-gray-700 border-gray-200';
};

const serviceStatusLabel = (value) => {
    if (value === 'ok') return 'Hazır';
    if (value === 'fallback') return 'Fallback';
    if (value === 'degraded') return 'Sorunlu';
    return value || 'Bilinmiyor';
};

const modelDetailLabel = (value) => {
    if (value === 'loaded') return 'Yüklendi';
    if (value === 'missing') return 'Dosya bekleniyor';
    if (value === 'missing_or_invalid') return 'Eksik veya geçersiz';
    return value || 'Bilinmiyor';
};

const modelVariantLabel = (value) => {
    if (value === 'derived-disease-backbone') return 'Türetilmiş';
    if (value === 'texture-proxy') return 'Geçici proxy';
    return value || '';
};

function AnalysisCard({ analysis }) {
    const hiveLabel = analysis.hive_name || (analysis.hive_id ? `Kovan #${analysis.hive_id}` : 'Genel analiz');

    return (
        <div className="bg-white rounded-xl shadow border border-gray-100 p-5 space-y-4">
            <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                <div>
                    <div className="flex items-center gap-2 text-sm text-amber-700 font-bold">
                        <Bot size={16} />
                        {analysis.is_demo ? 'Demo analiz motoru' : analysis.engine_name}
                    </div>
                    <h2 className="text-xl font-black text-gray-900 mt-2">{hiveLabel}</h2>
                    <p className="text-sm text-gray-500 mt-1">
                        Odak: {analysis.focus_mode || 'general'} · {formatDate(analysis.created_at)}
                    </p>
                </div>
                <div className="flex flex-wrap gap-2">
                    <span className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-xs font-bold text-gray-700">
                        Güven: %{Math.round(Number(analysis.confidence_score || 0) * 100)}
                    </span>
                    {analysis.hive_id ? (
                        <Link to={`/hive/${analysis.hive_id}`} className="inline-flex items-center rounded-full bg-amber-100 px-3 py-1 text-xs font-bold text-amber-800">
                            Kovaya git
                        </Link>
                    ) : null}
                </div>
            </div>

            {analysis.photo_url ? (
                <div className="overflow-hidden rounded-xl border border-gray-100 bg-gray-50">
                    <img src={analysis.photo_url} alt="Analiz edilen gorsel" className="w-full max-h-[380px] object-contain bg-gray-50" />
                </div>
            ) : null}

            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
                <p className="text-sm font-medium text-amber-950">{analysis.summary}</p>
            </div>

            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                {(analysis.metrics || []).map((metric) => (
                    <div key={metric.key} className={`rounded-xl border p-4 ${toneClasses[metric.tone] || toneClasses.medium}`}>
                        <div className="flex items-start justify-between gap-3">
                            <div className="text-xs font-bold uppercase tracking-wide opacity-80">{metric.label}</div>
                            {metric.status_label ? (
                                <span className="rounded-full bg-white/70 px-2 py-1 text-[11px] font-bold text-gray-700">
                                    {metric.status_label}
                                </span>
                            ) : null}
                        </div>
                        <div className="mt-2 text-2xl font-black">
                            {metric.value}
                            <span className="ml-1 text-sm font-semibold">{metric.unit}</span>
                        </div>
                        {metric.description ? (
                            <div className="mt-2 text-sm leading-5 opacity-90">{metric.description}</div>
                        ) : null}
                    </div>
                ))}
            </div>

            <div className="grid gap-4 lg:grid-cols-2">
                <div className="rounded-xl border border-gray-200 p-4">
                    <h3 className="font-black text-gray-900 mb-3">Tespitler</h3>
                    <div className="space-y-3">
                        {(analysis.detections || []).map((item, index) => (
                            <div key={`${item.label}-${index}`} className="rounded-lg border border-gray-100 bg-gray-50 p-3">
                                <div className="flex items-center justify-between gap-3">
                                    <div className="font-bold text-gray-900">{item.label}</div>
                                    <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${toneClasses[item.severity] || toneClasses.medium}`}>
                                        {severityLabel(item.severity)}
                                    </span>
                                </div>
                                <div className="mt-2 text-sm text-gray-600">{item.description}</div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="rounded-xl border border-gray-200 p-4">
                    <h3 className="font-black text-gray-900 mb-3">Öneriler</h3>
                    <div className="space-y-3">
                        {(analysis.recommendations || []).map((item, index) => (
                            <div key={`${item}-${index}`} className="rounded-lg border border-gray-100 bg-gray-50 p-3 text-sm text-gray-700">
                                {item}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default function AiAnalysis() {
    const [hives, setHives] = useState([]);
    const [analyses, setAnalyses] = useState([]);
    const [aiStatus, setAiStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [statusLoading, setStatusLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [form, setForm] = useState({
        hive_id: '',
        focus_mode: 'general',
        notes: ''
    });
    const [photo, setPhoto] = useState(null);
    const [preview, setPreview] = useState('');

    const latestAnalysis = analyses[0] || null;

    const focusOptions = useMemo(() => ([
        { value: 'general', label: 'Genel petek taraması' },
        { value: 'varroa', label: 'Varroa odaklı tarama' },
        { value: 'brood', label: 'Yavru düzeni taraması' },
        { value: 'reserves', label: 'Bal ve polen rezerv taraması' },
        { value: 'queen', label: 'Ana arı görünürlük taraması' }
    ]), []);

    const loadStatus = async () => {
        setStatusLoading(true);
        try {
            const statusRes = await axios.get('/api/ai/status');
            setAiStatus(statusRes.data || null);
        } catch (err) {
            setAiStatus({
                service: {
                    status: 'degraded',
                    engine: 'demo-heuristic-v1',
                    mode: 'fallback'
                },
                models: [
                    {
                        name: 'demo-heuristic-v1',
                        enabled: true,
                        available: true,
                        source: 'built-in',
                        details: err.response?.data?.error || 'AI servis durumu alınamadı.'
                    }
                ]
            });
        } finally {
            setStatusLoading(false);
        }
    };

    const loadPage = async () => {
        setLoading(true);
        setError('');
        try {
            const [ctxRes, analysesRes, statusRes] = await Promise.all([
                axios.get('/api/tools/context'),
                axios.get('/api/ai/analyses'),
                axios.get('/api/ai/status')
            ]);
            setHives(ctxRes.data.hives || []);
            setAnalyses(analysesRes.data.data || []);
            setAiStatus(statusRes.data || null);
        } catch (err) {
            setError(err.response?.data?.error || 'AI analiz verileri alınamadı.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadPage();
    }, []);

    useEffect(() => {
        if (!photo) {
            setPreview('');
            return undefined;
        }

        const objectUrl = URL.createObjectURL(photo);
        setPreview(objectUrl);
        return () => URL.revokeObjectURL(objectUrl);
    }, [photo]);

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!photo) {
            setError('Analiz için bir görsel seçmelisin.');
            return;
        }

        setSubmitting(true);
        setError('');
        try {
            const payload = new FormData();
            if (form.hive_id) payload.append('hive_id', form.hive_id);
            payload.append('focus_mode', form.focus_mode);
            payload.append('notes', form.notes);
            payload.append('photo', photo);

            const res = await axios.post('/api/ai/analyze', payload);
            setAnalyses((prev) => [res.data.data, ...prev]);
            setForm((prev) => ({ ...prev, notes: '' }));
            setPhoto(null);
            loadStatus();
        } catch (err) {
            setError(err.response?.data?.error || 'Analiz başlatılamadı.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-amber-50 via-white to-gray-50 pb-16">
            <div className="max-w-6xl mx-auto px-4 py-5 space-y-5">
                <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                    <div>
                        <Link to="/tools" className="inline-flex items-center gap-2 text-sm font-bold text-amber-700 hover:text-amber-800">
                            <ArrowLeft size={16} />
                            Araçlara dön
                        </Link>
                        <h1 className="text-3xl font-black text-gray-900 mt-2 flex items-center gap-3">
                            <Microscope className="text-amber-600" />
                            AI Petek Analizi
                        </h1>
                        <p className="text-gray-600 mt-2 max-w-3xl">
                            Fotograf yukleyip kovan bazinda AI analiz akisini calistirabilirsin. Bu ekran, yuklu ONNX modelleri ile fallback motorunu birlikte kullanan hibrit analiz durumunu gosterir.
                        </p>
                    </div>
                </div>

                <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950 flex gap-3">
                    <AlertTriangle className="shrink-0 mt-0.5" size={18} />
                    <div>
                        Bu ekran aktif AI motorunu ve model hazır olma durumunu gösterir. Kesin teşhis yerine saha kontrolü ve uzman doğrulaması esas alınmalıdır.
                    </div>
                </div>

                {error ? (
                    <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                        {error}
                    </div>
                ) : null}

                <div className="grid gap-5 xl:grid-cols-[420px_minmax(0,1fr)]">
                    <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow border border-gray-100 p-5 space-y-4 h-fit">
                        <div>
                            <h2 className="text-xl font-black text-gray-900">Yeni analiz oluştur</h2>
                            <p className="text-sm text-gray-500 mt-1">Görsel yükle, odak modunu seç ve sonucu geçmişe kaydet.</p>
                        </div>

                        <div className="space-y-2">
                            <label className="text-sm font-bold text-gray-700">Kovan</label>
                            <select value={form.hive_id} onChange={(e) => setForm((prev) => ({ ...prev, hive_id: e.target.value }))} className="w-full border rounded-xl px-4 py-3">
                                <option value="">Genel analiz</option>
                                {hives.map((hive) => (
                                    <option key={hive.id} value={hive.id}>{hive.name}</option>
                                ))}
                            </select>
                        </div>

                        <div className="space-y-2">
                            <label className="text-sm font-bold text-gray-700">Analiz modu</label>
                            <select value={form.focus_mode} onChange={(e) => setForm((prev) => ({ ...prev, focus_mode: e.target.value }))} className="w-full border rounded-xl px-4 py-3">
                                {focusOptions.map((option) => (
                                    <option key={option.value} value={option.value}>{option.label}</option>
                                ))}
                            </select>
                        </div>

                        <div className="space-y-2">
                            <label className="text-sm font-bold text-gray-700">Fotoğraf</label>
                            <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => setPhoto(e.target.files?.[0] || null)}
                                className="w-full border rounded-xl px-4 py-3 bg-white"
                            />
                            {preview ? (
                                <div className="overflow-hidden rounded-xl border border-gray-100 bg-gray-50">
                                    <img src={preview} alt="Onizleme" className="w-full max-h-64 object-contain bg-gray-50" />
                                </div>
                            ) : null}
                        </div>

                        <div className="space-y-2">
                            <label className="text-sm font-bold text-gray-700">Operatör notu</label>
                            <textarea
                                value={form.notes}
                                onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
                                rows="4"
                                className="w-full border rounded-xl px-4 py-3"
                                placeholder="Örn: Açık yavru alanında düzensizlik ve boş göz yoğunluğu dikkat çekiyor."
                            />
                        </div>

                        <button
                            type="submit"
                            disabled={submitting}
                            className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-amber-500 px-4 py-3 font-bold text-white hover:bg-amber-600 disabled:opacity-60"
                        >
                            {submitting ? <Loader2 size={18} className="animate-spin" /> : <Sparkles size={18} />}
                            {submitting ? 'Analiz hazırlanıyor...' : 'Analizi başlat'}
                        </button>
                    </form>

                    <div className="space-y-5">
                        <div className="bg-white rounded-xl shadow border border-gray-100 p-5 space-y-4">
                            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                                <div>
                                    <h2 className="text-lg font-black text-gray-900 flex items-center gap-2">
                                        <Cpu size={18} className="text-amber-600" />
                                        AI Motor Durumu
                                    </h2>
                                    <p className="text-sm text-gray-500 mt-1">Aktif motor, servis modu ve model yük durumları.</p>
                                </div>
                                <button
                                    type="button"
                                    onClick={loadStatus}
                                    disabled={statusLoading}
                                    className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-200 px-4 py-2 text-sm font-bold text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                                >
                                    <RefreshCw size={16} className={statusLoading ? 'animate-spin' : ''} />
                                    Yenile
                                </button>
                            </div>

                            <div className="grid gap-3 md:grid-cols-3">
                                <div className={`rounded-xl border p-4 ${serviceStatusClasses(aiStatus?.service?.status)}`}>
                                    <div className="text-xs font-bold uppercase tracking-wide opacity-80">Servis durumu</div>
                                    <div className="mt-2 text-2xl font-black">{serviceStatusLabel(aiStatus?.service?.status)}</div>
                                </div>
                                <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 text-gray-800">
                                    <div className="text-xs font-bold uppercase tracking-wide opacity-70">Aktif motor</div>
                                    <div className="mt-2 text-lg font-black break-all">{aiStatus?.service?.engine || 'Bilinmiyor'}</div>
                                </div>
                                <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 text-gray-800">
                                    <div className="text-xs font-bold uppercase tracking-wide opacity-70">Çalışma modu</div>
                                    <div className="mt-2 text-lg font-black">{aiStatus?.service?.mode || 'Bilinmiyor'}</div>
                                </div>
                            </div>

                            <div className="space-y-3">
                                {(aiStatus?.models || []).length ? (
                                    aiStatus.models.map((model) => (
                                        <div key={model.name} className="rounded-xl border border-gray-100 p-4 space-y-3">
                                            <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                                                <div className="min-w-0">
                                                    <div className="flex items-center gap-2">
                                                        <Activity size={16} className="text-amber-600 shrink-0" />
                                                        <div className="font-bold text-gray-900 break-all">{model.label || model.name}</div>
                                                    </div>
                                                    <div className="text-sm text-gray-500 mt-1 break-all">{model.source}</div>
                                                    {model.expected_filename ? (
                                                        <div className="text-xs text-gray-500 mt-1">
                                                            Beklenen dosya: <span className="font-bold text-gray-700">{model.expected_filename}</span>
                                                        </div>
                                                    ) : null}
                                                </div>
                                                <div className="flex flex-wrap gap-2">
                                                    <span className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-bold ${model.available ? 'bg-green-50 text-green-800 border-green-200' : 'bg-gray-50 text-gray-700 border-gray-200'}`}>
                                                        {model.available ? 'Yüklü' : 'Yok'}
                                                    </span>
                                                    <span className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-bold ${model.enabled ? 'bg-blue-50 text-blue-800 border-blue-200' : 'bg-gray-50 text-gray-700 border-gray-200'}`}>
                                                        {model.enabled ? 'Etkin' : 'Pasif'}
                                                    </span>
                                                    {model.variant ? (
                                                        <span className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-bold ${model.provisional ? 'bg-amber-50 text-amber-800 border-amber-200' : 'bg-purple-50 text-purple-800 border-purple-200'}`}>
                                                            {modelVariantLabel(model.variant)}
                                                        </span>
                                                    ) : null}
                                                    <span className="inline-flex items-center rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-xs font-bold text-gray-700">
                                                        {modelDetailLabel(model.details)}
                                                    </span>
                                                </div>
                                            </div>

                                            {(model.expected_input_shape?.length || model.supported_output_shapes?.length || model.loaded_inputs?.length || model.loaded_outputs?.length || model.notes) ? (
                                                <div className="grid gap-3 lg:grid-cols-2">
                                                    <div className="rounded-lg border border-gray-100 bg-gray-50 p-3 text-sm text-gray-700 space-y-2">
                                                        <div>
                                                            <span className="font-bold text-gray-900">Beklenen giriş:</span>{' '}
                                                            {(model.expected_input_shape || []).join(' x ') || '-'}
                                                        </div>
                                                        <div>
                                                            <span className="font-bold text-gray-900">Desteklenen çıkış:</span>{' '}
                                                            {(model.supported_output_shapes || []).join(', ') || '-'}
                                                        </div>
                                                        {model.notes ? (
                                                            <div>
                                                                <span className="font-bold text-gray-900">Not:</span> {model.notes}
                                                            </div>
                                                        ) : null}
                                                        {model.derived_from ? (
                                                            <div>
                                                                <span className="font-bold text-gray-900">Türetilen kaynak:</span> {model.derived_from}
                                                            </div>
                                                        ) : null}
                                                        {typeof model.score_weight === 'number' && model.score_weight < 1 ? (
                                                            <div>
                                                                <span className="font-bold text-gray-900">Model ağırlığı:</span> %{Math.round(model.score_weight * 100)}
                                                            </div>
                                                        ) : null}
                                                    </div>

                                                    <div className="rounded-lg border border-gray-100 bg-gray-50 p-3 text-sm text-gray-700 space-y-2">
                                                        <div>
                                                            <span className="font-bold text-gray-900">Yüklenen input:</span>{' '}
                                                            {(model.loaded_inputs || []).length ? model.loaded_inputs.join(', ') : '-'}
                                                        </div>
                                                        <div>
                                                            <span className="font-bold text-gray-900">Yüklenen output:</span>{' '}
                                                            {(model.loaded_outputs || []).length ? model.loaded_outputs.join(', ') : '-'}
                                                        </div>
                                                    </div>
                                                </div>
                                            ) : null}
                                        </div>
                                    ))
                                ) : (
                                    <div className="rounded-xl border border-dashed border-gray-200 p-4 text-sm text-gray-500">
                                        Henüz model durum verisi yok. `GET /api/ai/status` ile servis görünürlüğünü doğrulayabilirsin.
                                    </div>
                                )}
                            </div>
                        </div>

                        {loading ? (
                            <div className="bg-white rounded-xl shadow border border-gray-100 p-6 flex items-center gap-3 text-gray-600">
                                <Loader2 size={18} className="animate-spin" />
                                AI analiz kayıtları yükleniyor...
                            </div>
                        ) : null}

                        {!loading && latestAnalysis ? <AnalysisCard analysis={latestAnalysis} /> : null}

                        {!loading && !latestAnalysis ? (
                            <div className="bg-white rounded-xl shadow border border-dashed border-gray-200 p-8 text-center text-gray-500">
                                Henüz analiz kaydı yok. İlk petek fotoğrafını yükleyip AI akışını başlatabilirsin.
                            </div>
                        ) : null}

                        {!loading && analyses.length > 1 ? (
                            <div className="bg-white rounded-xl shadow border border-gray-100 p-5">
                                <h2 className="text-lg font-black text-gray-900 mb-4">Geçmiş analizler</h2>
                                <div className="space-y-3">
                                    {analyses.slice(1).map((analysis) => (
                                        <div key={analysis.id} className="rounded-xl border border-gray-100 p-4 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                                            <div>
                                                <div className="font-bold text-gray-900">{analysis.hive_name || 'Genel analiz'}</div>
                                                <div className="text-sm text-gray-500">
                                                    {formatDate(analysis.created_at)} · Sağlık {analysis.health_score}/100 · Varroa {analysis.varroa_risk}/100
                                                </div>
                                            </div>
                                            <div className="text-sm text-gray-600">{analysis.summary}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>
        </div>
    );
}
